import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Import our modular step components
import {
  CommunityProfile,
  FinancialAssessment,
  CurrentChallenges,
  TechnologyRequirements,
  StakeholderMapping,
  ImplementationReadiness,
  VisionAndGoals,
  EnterpriseRequirements,
} from '@/components/onboarding/customer/pricing-page/enterprise/steps';

// Import types and hooks
import { SalesQualificationData } from '@/types/onboarding/salesQualification';
import { useSalesQualification } from '@/hooks/entities/onboarding/useSalesQualification';

export const SalesQualificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tier = (searchParams.get('tier') || 'custom') as 'silver' | 'gold' | 'custom';
  const [currentStep, setCurrentStep] = useState(1);

  // Use the sales qualification hook
  const { isSubmitting, submitQualification, error, clearError } = useSalesQualification();

  const [formData, setFormData] = useState<Partial<SalesQualificationData>>({
    decisionMakers: [
      {
        name: '',
        title: '',
        email: '',
        influence: '',
        role: '',
      },
    ],
  });

  const totalSteps = 8;

  useEffect(() => {
    // Auto-load form data from localStorage for persistence
    const savedData = localStorage.getItem('salesQualificationData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
      } catch (error) {
        console.error('Error parsing saved form data:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Save form data to localStorage on changes
    localStorage.setItem('salesQualificationData', JSON.stringify(formData));
  }, [formData]);

  const updateFormData = (updates: Partial<SalesQualificationData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    if (error) clearError();

    try {
      // Submit comprehensive qualification data
      const result = await submitQualification(formData as SalesQualificationData, tier);

      if (result.success) {
        // Clear saved data on successful submission
        localStorage.removeItem('salesQualificationData');

        // Navigate to success page with assessment ID
        navigate(
          `/onboarding/success?type=sales-qualification&tier=${tier}&assessmentId=${result.assessmentId}`,
        );
      } else {
        // Error handling is done by the hook
        console.error('Qualification submission failed:', result.message);
      }
    } catch (error) {
      console.error('Unexpected submission error:', error);
    }
  };

  const renderCurrentStep = () => {
    const stepProps = { data: formData, onUpdate: updateFormData };

    switch (currentStep) {
      case 1:
        return <CommunityProfile {...stepProps} />;
      case 2:
        return <FinancialAssessment {...stepProps} />;
      case 3:
        return <CurrentChallenges {...stepProps} />;
      case 4:
        return <TechnologyRequirements {...stepProps} />;
      case 5:
        return <StakeholderMapping {...stepProps} />;
      case 6:
        return <ImplementationReadiness {...stepProps} />;
      case 7:
        return <VisionAndGoals {...stepProps} />;
      case 8:
        return <EnterpriseRequirements {...stepProps} />;
      default:
        return <CommunityProfile {...stepProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-xs border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() =>
                currentStep === 1
                  ? navigate('/onboarding/welcome/contact-sales')
                  : setCurrentStep((prev) => prev - 1)
              }
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="text-sm text-gray-500">
              Step {currentStep} of {totalSteps}: Sales Qualification
            </div>
            <Badge className="bg-linear-to-r from-purple-600 to-indigo-600 text-white">
              Custom Enterprise
            </Badge>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-linear-to-r from-purple-600 to-indigo-600 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div key={i} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      i + 1 < currentStep
                        ? 'bg-green-500 text-white'
                        : i + 1 === currentStep
                          ? 'bg-white text-gray-900'
                          : 'bg-white/30 text-white/70'
                    }`}
                  >
                    {i + 1 < currentStep ? <CheckCircle className="w-5 h-5" /> : i + 1}
                  </div>
                  {i < totalSteps - 1 && (
                    <div
                      className={`w-8 h-0.5 ${
                        i + 1 < currentStep ? 'bg-green-500' : 'bg-white/30'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-center">
                  Custom Enterprise Qualification
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">{renderCurrentStep()}</CardContent>
            </Card>

            {/* Error Display */}
            {error && (
              <Card className="mt-4 border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <span className="text-red-500">⚠️</span>
                    <span className="font-medium">Submission Error:</span>
                    <span>{error}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearError}
                      className="ml-auto text-red-600 hover:text-red-700"
                    >
                      ×
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={() =>
                  currentStep === 1
                    ? navigate('/onboarding/welcome/contact-sales')
                    : setCurrentStep((prev) => prev - 1)
                }
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {currentStep === 1 ? 'Back to Welcome' : 'Previous Step'}
              </Button>

              <Button
                onClick={() =>
                  currentStep === totalSteps ? handleSubmit() : setCurrentStep((prev) => prev + 1)
                }
                disabled={isSubmitting}
                className="flex items-center gap-2 bg-linear-to-r from-purple-600 to-indigo-600 text-white"
              >
                {currentStep === totalSteps ? (
                  isSubmitting ? (
                    'Submitting...'
                  ) : (
                    'Complete Qualification'
                  )
                ) : (
                  <>
                    Next Step
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>

            {/* Help Text */}
            {/* <div className="text-center mt-6">
              <p className="text-sm text-gray-500">
                Need help? <a href="mailto:support@example.com" className="text-purple-600 hover:underline">
                  Contact our enterprise team
                </a>
              </p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};
